import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { startActivityTracking, stopActivityTracking } from '../utils/activityTracker';
import { isTokenExpired, decodeToken } from '../utils/tokenValidator';

interface User {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  organizationName?: string;
  organizationRole?: 'owner' | 'admin' | 'member';
  role: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  
  // Actions
  login: (user: User, token: string, refreshToken?: string) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  setToken: (token: string) => void;
  setAuth: (user: User, token: string) => void;
  initialize: () => void;
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    isAuthenticated: false,
    user: null,
    token: null,
    refreshToken: null,

    login: (user, token, refreshToken) => {
      console.log('[AuthStore] Login called with:', { user, token: !!token, refreshToken: !!refreshToken });
      console.log('[AuthStore] Login user organizationRole:', user?.organizationRole);
      console.log('[AuthStore] Login user organizationRole type:', typeof user?.organizationRole);
      console.log('[AuthStore] Login user organizationRole === "owner":', user?.organizationRole === 'owner');
      
      // Store token in localStorage for API client
      localStorage.setItem('authToken', token);
      localStorage.setItem('token', token);
      
      // Store refresh token if provided
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      
      // Store user data separately
      localStorage.setItem('user', JSON.stringify(user));
      console.log('[AuthStore] Stored user in localStorage:', JSON.stringify(user));
      
      set({
        isAuthenticated: true,
        user,
        token,
        refreshToken: refreshToken || null,
      });
      
      // Start activity tracking for automatic timeout
      startActivityTracking();
      
      // Verify final auth store state
      const finalState = get();
      console.log('[AuthStore] Final login state user:', finalState.user);
      console.log('[AuthStore] Final login state organizationRole:', finalState.user?.organizationRole);
    },

    logout: () => {
      console.log('[AuthStore] Logout called');
      
      // Stop activity tracking
      stopActivityTracking();
      
      // Remove all auth data from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      
      set({
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
      });
    },

    updateUser: (updates) =>
      set((state) => {
        const updatedUser = state.user ? { ...state.user, ...updates } : null;
        if (updatedUser) {
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        return { user: updatedUser };
      }),

    setToken: (token) => {
      localStorage.setItem('authToken', token);
      localStorage.setItem('token', token);
      set({ token });
    },
    
    setAuth: (user, token) => {
      console.log('[AuthStore] SetAuth called with:', { user, token: !!token });
      console.log('[AuthStore] User organizationRole in setAuth:', user?.organizationRole);
      
      // Store token in localStorage for API client
      localStorage.setItem('authToken', token);
      localStorage.setItem('token', token);
      console.log('[AuthStore] Token stored in localStorage:', localStorage.getItem('token'));
      
      // Store user data separately
      localStorage.setItem('user', JSON.stringify(user));
      console.log('[AuthStore] User stored in localStorage:', localStorage.getItem('user'));
      
      // Let's also decode the token to see what's in it
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('[AuthStore] Token payload in setAuth:', payload);
        console.log('[AuthStore] organizationRole in token payload:', payload.organizationRole);
      } catch (e) {
        console.error('[AuthStore] Failed to decode token in setAuth:', e);
      }
      
      set({
        isAuthenticated: true,
        user,
        token,
        refreshToken: null,
      });
      
      // Start activity tracking for automatic timeout
      startActivityTracking();
      
      // Verify state was set
      const newState = get();
      console.log('[AuthStore] State after setAuth:', {
        isAuthenticated: newState.isAuthenticated,
        user: newState.user,
        token: !!newState.token
      });
    },
    
    initialize: () => {
      console.log('[AuthStore] Initialize called');
      
      // Use same priority as other parts of the app: authToken first, then token
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      const userStr = localStorage.getItem('user');
      
      console.log('[AuthStore] Initialize - token:', !!token, 'refreshToken:', !!refreshToken, 'userStr:', !!userStr);
      
      // Check if token is expired before proceeding
      if (token) {
        if (isTokenExpired(token)) {
          console.log('[AuthStore] Token expired during initialization, clearing auth');
          localStorage.removeItem('authToken');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');
          
          // Dispatch auth error event
          const event = new CustomEvent('auth-error', { 
            detail: { message: 'Your session has expired. Please log in again.' } 
          });
          window.dispatchEvent(event);
          
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
          });
          return;
        }
      }
      
      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          console.log('[AuthStore] Raw userStr from localStorage:', userStr);
          console.log('[AuthStore] Parsed user object:', user);
          console.log('[AuthStore] Restoring auth from localStorage:', { user, token: !!token, refreshToken: !!refreshToken });
          console.log('[AuthStore] Restored user organizationRole:', user?.organizationRole);
          console.log('[AuthStore] Restored user organizationRole type:', typeof user?.organizationRole);
          console.log('[AuthStore] Restored user organizationRole === "owner":', user?.organizationRole === 'owner');
          
          // Validate user object has required fields
          if (!user.id || !user.email) {
            console.error('[AuthStore] Invalid user object - missing required fields');
            throw new Error('Invalid user object');
          }
          
          // Force type checking on organizationRole to ensure it's properly typed
          const typedUser = {
            ...user,
            organizationRole: user.organizationRole as 'owner' | 'admin' | 'member' | undefined
          };
          
          console.log('[AuthStore] Type-corrected user:', typedUser);
          console.log('[AuthStore] Type-corrected organizationRole:', typedUser.organizationRole);
          
          set({
            isAuthenticated: true,
            user: typedUser,
            token,
            refreshToken: refreshToken || null,
          });
          
          // Start activity tracking for restored session
          startActivityTracking();
          
          // Verify final state after restoration
          const restoredState = get();
          console.log('[AuthStore] Final restored state user:', restoredState.user);
          console.log('[AuthStore] Final restored state organizationRole:', restoredState.user?.organizationRole);
          
          console.log('[AuthStore] Successfully restored auth state');
        } catch (error) {
          console.error('[AuthStore] Failed to restore auth:', error);
          console.error('[AuthStore] userStr was:', userStr);
          // Don't clear data immediately - this might be a temporary issue
          console.warn('[AuthStore] NOT clearing localStorage - keeping data for debugging');
        }
      } else if (token) {
        // Try to decode token if we have it but no user
        const payload = decodeToken(token);
        if (payload) {
            console.log('[AuthStore] Token payload:', payload);
            console.log('[AuthStore] All payload keys:', Object.keys(payload));
            console.log('[AuthStore] organizationRole in payload:', payload.organizationRole);
            console.log('[AuthStore] organization_role in payload:', payload.organization_role);
            
            // Ensure we use the exact field from the token
            const organizationRole = payload.organizationRole || payload.organization_role;
            
            // Add extra debug logging for the extraction
            console.log('[AuthStore] Extracted organizationRole:', organizationRole);
            console.log('[AuthStore] Extracted organizationRole type:', typeof organizationRole);
            console.log('[AuthStore] Extracted organizationRole === "owner":', organizationRole === 'owner');
            
            const user = {
              id: payload.sub || payload.userId,
              email: payload.email,
              name: payload.name || `${payload.firstName || ''} ${payload.lastName || ''}`.trim(),
              firstName: payload.firstName,
              lastName: payload.lastName,
              organizationId: payload.organizationId || payload.organization_id,
              organizationName: payload.organizationName || payload.organization_name,
              organizationRole: organizationRole as 'owner' | 'admin' | 'member' | undefined,
              role: payload.role || 'user',
            };
            
            console.log('[AuthStore] Decoded user from token:', user);
            console.log('[AuthStore] Final user organizationRole:', user.organizationRole);
            console.log('[AuthStore] Final user organizationRole type:', typeof user.organizationRole);
            console.log('[AuthStore] Final user organizationRole === "owner":', user.organizationRole === 'owner');
            
            localStorage.setItem('user', JSON.stringify(user));
            
            set({
              isAuthenticated: true,
              user,
              token,
              refreshToken: refreshToken || null,
            });
            
            // Start activity tracking for decoded session
            startActivityTracking();
            
            // Verify the state was set correctly
            const finalState = get();
            console.log('[AuthStore] Final state after token decode:', finalState.user);
            console.log('[AuthStore] Final state organizationRole:', finalState.user?.organizationRole);
        }
      }
    },
  }))
);

// Initialize on app load
if (typeof window !== 'undefined') {
  console.log('[AuthStore] Setting up initialization');
  // Use setTimeout to ensure store is fully created
  setTimeout(() => {
    console.log('[AuthStore] Calling initialize from global scope');
    useAuthStore.getState().initialize();
  }, 0);
}

// Expose store globally for API interceptor
if (typeof window !== 'undefined') {
  (window as any).__authStore = useAuthStore;
}